import { useState, useContext, useRef, useEffect, useMemo } from "react";
import moment from "moment";
import { SnackBarContext } from "components/snack-bar";
import { toFixed2 } from "utils/formators";
import { useStore } from "store";
import { useHistory } from "react-router-dom";
import { decrypt } from "pages/auth/utils";
import { CAMPAIGN_INTERNAL_STATUSES, COUPON_CODE_ERROR_MESSAGE } from "pages/constants";
import { CAMPAIGN_LOG_ENUM_TYPES, CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import { apiClient } from "module/api";
import { getCurrencyCode } from "utils";
import { insertCampaignLogs } from "Logs/campaign/gql";
import { FeatureFlagContext } from "context/featureFlagContext";
import {
  updateCampaignExtraData,
  updateSelfServeCampaignData,
  updateCampaign as updateCampaignDB,
  doCheckoutGQL,
} from "../../graphQL";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useCampaignCostPreviewCalculation } from "campaign-builder/hooks/useCampaignCostPreviewCalculation";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { normalizeCampaignName } from "campaign-builder/state";
import { getPaymentSkipStatus } from "utils/paymentSkip";
import { useBillingPreview } from "campaign-builder/checkout/hooks/useBillingPreview";
import { deriveCheckoutDiscountChoice, CheckoutDiscountChoice } from "./deriveCheckoutDiscountChoice";

interface UseADMSubmitProps {
  setIsCampaignSubmissionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * HUB-458: the order drawer's useDiscountExclusivity.discountChoice,
   * lifted up via OrderDetailsDrawer's onDiscountChoiceChange. `undefined`
   * until the drawer's first effect runs (treated the same as `null` — no
   * discount) so submit never blocks waiting on it.
   */
  discountChoice?: CheckoutDiscountChoice;
}

export const useADMSubmit = ({ setIsCampaignSubmissionModalOpen, discountChoice = null }: UseADMSubmitProps) => {
  const runSnackBar = useContext(SnackBarContext);
  const history = useHistory();
  const { featureFlags, loading: flagsLoading } = useContext(FeatureFlagContext);
  const isReviewDiscountEnabled = !flagsLoading && !!featureFlags.isReviewDiscountEnabled;
  const [submitting, setSubmitting] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const { campaignData, clientId, user, refetchCampaign, country, setIsReadOnly } = useCampaignData();
  const { client } = useClientConfig();
  const { costPerFlyer, flyersAmount, campaignCostPreview, dataCost } = useCampaignCostPreviewCalculation();
  const { tax, subtotal, total } = campaignCostPreview || {};
  const [couponErrorMessage, setCouponErrorMessage] = useState("");
  const {
    id: campaignId,
    stripeCouponCode,
    startDate,
    flyerType,
    campaignName,
    amountOff,
    percentOff,
  } = campaignData || {};

  const isPaymentSkipped = useMemo(() => {
    return getPaymentSkipStatus(campaignData, client);
  }, [campaignData?.isPaymentSkipped, client?.isSkipCheckoutEnabled]);

  const promotionIdRef = useRef("");
  const customerIdRef = useRef("");
  const isSubmittingRef = useRef(false);

  const { costsCalculationData } = useStore();
  const { detailedCost } = costsCalculationData;

  useEffect(() => {
    if (history?.location?.search?.split("payment=")[1] && campaignId) {
      const decryptedCampaignId = decrypt(history?.location?.search.split("payment=")[1]);
      window.history.replaceState(null, "", `${window.location.href.split("?payment")[0]}`);
      if (decryptedCampaignId && String(decryptedCampaignId) === String(campaignId)) {
        setIsReadOnly(true);
        // Campaign transition to IN_REVIEW is handled server-side by Temporal
        // after payment authorization. Just refetch to get the latest state.
        setIsCampaignSubmissionModalOpen(true);
        refetchCampaign();
      } else {
        runSnackBar({
          type: "error",
          msg: `Payment error.`,
          vertical: "bottom",
          horizontal: "right",
        });
      }
    }
  }, [history?.location?.search, campaignId]);

  useEffect(() => {
    if (!stripeCouponCode && promotionIdRef.current) {
      promotionIdRef.current = "";
    }
  }, [stripeCouponCode]);

  const onUpdateCampaignCostWithCoupon = async (
    couponCode: string | null,
    amountOff: number | null,
    percentOff: number | null
  ) => {
    const payloadCampaignExtraData = {
      campaignId: campaignData?.id,
      stripeCouponCode: couponCode,
      amountOff,
      percentOff,
    };
    await updateCampaignExtraData(payloadCampaignExtraData);
  };

  const getGrandForStripe = () => {
    let grand = Number(Number(total) + (Number(amountOff) || 0));
    if (percentOff && percentOff < 100) {
      /**
       * on stripe side in case we applied percent off coupon
       * grand - grand * percentOff / 100 = totalCost
       */
      grand = (total * 100) / (100 - percentOff);
    }

    return Math.trunc(Number((Number(grand) * 100).toFixed(2)));
  };

  const updatePathfinderData = async () => {
    await updateSelfServeCampaignData({
      campaignId: campaignId,
      campaignDetailedCosts: {
        amountOff: amountOff || 0,
        percentOff: percentOff || 0,
        tax,
        subtotal,
        totalCost: total,
      },
    });
  };

  const logCampaignSubmitted = async () => {
    await insertCampaignLogs([
      {
        campaignId,
        type: CAMPAIGN_LOG_ENUM_TYPES.CAMPAIGN_SUBMITTED,
        additionalInfo: `[CB] CAMPAIGN EDIT Subtotal: ${campaignCostPreview?.subtotal}, Amount off: ${campaignCostPreview?.amountOff}, Total: ${campaignCostPreview?.total}`,
      },
      {
        campaignId,
        type: CAMPAIGN_LOG_ENUM_TYPES.SET_PAYMENT_STATUS,
        additionalInfo: `[CB] Status: [uncaptured]`,
      },
    ]);
  };

  const updateCampaignAndCampaignExtraData = async () => {
    const parsedDate = `${moment(Number(startDate)).format("YYYY-MM-DD")}T00:00:00.000Z`;
    await updateCampaignDB({
      campaignId,
      name: campaignName ? normalizeCampaignName(campaignName) : campaignName,
      printingCostPerFlyer: costPerFlyer,
      startDate: parsedDate,
    });

    await updateCampaignExtraData({
      campaignId,
      stripeCouponCode,
      startDate: parsedDate,
      flyerType,
      totalCosts: total,
      taxes: tax,
      submitterId: user.id,
      flyersCount: flyersAmount,
    });
  };

  const fetchStripeCheckout = async () => {
    const promotionId = promotionIdRef.current || "";
    // HUB-458 (epic HUB-440): the checkout request must carry the client's
    // explicit "one discount per order" intent —
    // docs/review-discount-payments-integration.md §2.0. Server-authoritative
    // (§2.1: "on any error/timeout, log and continue without a discount"),
    // so this is a best-effort snapshot of the drawer's current
    // discountChoice at submit time, not a fresh re-fetch — cheap and never
    // blocks submit, per the same doc's guidance.
    const checkoutDiscountChoice = deriveCheckoutDiscountChoice({
      isReviewDiscountEnabled,
      discountChoice,
      hasStripeCoupon: !!stripeCouponCode,
    });
    const res = await apiClient.embeddedCheckoutCampaign({
      campaignId,
      clientId: clientId,
      campaignName: `Campaign ${campaignName ? normalizeCampaignName(campaignName) : campaignName}`,
      grand: getGrandForStripe(),
      currencyCode: getCurrencyCode(country),
      countryCode: country?.code,
      promotionId,
      customerId: customerIdRef.current || "",
      userId: user?.id,
      subtype: campaignData?.subtype,
      campaignDetailedCosts: {
        tax,
        subtotal,
        totalCost: total,
        amountOff: amountOff || 0,
        percentOff: percentOff || 0,
        dataCost: dataCost || 0,
      },
      discountChoice: checkoutDiscountChoice,
    });

    if (res.status === 200) {
      const { clientSecret } = await res.json();

      return clientSecret;
    }
  };

  const sideEffects = async ({ doCheckout = false }: { doCheckout?: boolean }) => {
    try {
      // Note: isSubmittingRef is managed by handleSubmit when called from submission flow
      // This function is only called from handleSubmit (guarded) or useEffect (one-time redirect)
      setIsCampaignSubmissionModalOpen(true);

      if (doCheckout) {
        try {
          // Fetch cart data when checkout is needed
          await doCheckoutGQL({
            campaignId,
            campaignDetailedCosts: {
              ...detailedCost,
              subtotal: toFixed2(subtotal),
              tax: toFixed2(tax),
              totalCost: toFixed2(total),
              amountOff: amountOff || 0,
              percentOff: percentOff || 0,
              dataCost: dataCost || 0,
            },
          });
        } catch (error) {
          console.error("Failed to fetch cart data:", error);
          runSnackBar({
            type: "error",
            msg: "Failed to fetch cart information.",
            vertical: "bottom",
            horizontal: "right",
          });
        }
      }

      await updateCampaignExtraData({
        campaignId,
        paymentStatus: "uncaptured",
        internalStatus: CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
        lastActiveStep: "submit",
        subtype: CAMPAIGN_SUBTYPES_ENUM.ADM,
      });
      refetchCampaign();
    } catch (e) {
      runSnackBar({
        type: "error",
        msg: `Error while submitting the campaign.`,
        vertical: "bottom",
        horizontal: "right",
      });
    }
  };

  const handleSubmit = async () => {
    // Prevent duplicate submissions
    if (isSubmittingRef.current) {
      return;
    }

    try {
      isSubmittingRef.current = true;

      // Refuse to submit a campaign priced at 0 (PAD-32595). The cost is computed
      // entirely client-side and written straight to pathfinder and
      // CampaignExtraData; nothing server-side ever recomputes it, so a 0 that
      // gets through here is what ops and the client's invoice will see. Stripe
      // used to catch this implicitly — it cannot charge $0 — but skip-checkout
      // removes that step, which is why the bad total only ever surfaced on
      // skip-stripe campaigns. Make the check explicit so both paths are covered.
      // Checked on the pre-discount subtotal, not the total: a 100%-off coupon
      // legitimately drives the total to 0, and blocking that would refuse a
      // valid free campaign. A 0 subtotal only ever means the audience did not
      // resolve.
      if (!subtotal || subtotal <= 0) {
        throw new Error(
          "The campaign cost is still 0 — please make sure an audience is selected and its data has loaded, then try again."
        );
      }

      if (stripeCouponCode) {
        if (!(await handleApplyCouponCode({ stripeCouponCode }))) {
          throw new Error("Sorry, this coupon code is not active any more.");
        }
      }
      await updatePathfinderData();
      // Logging must happen before campaign refetch to avoid race condition
      // that would cause price data (subtotal, total, tax) to be 0 in logs
      // since the refetch would clear the current values
      await logCampaignSubmitted();
      await updateCampaignAndCampaignExtraData();
      if (!isPaymentSkipped) {
        setShowCheckout(true);
      } else {
        await sideEffects({ doCheckout: true });
      }
    } catch (error) {
      isSubmittingRef.current = false;
      runSnackBar({
        type: "error",
        msg: `Failed to submit campaign. ${(error as any)?.message || ""}`,
        vertical: "bottom",
        horizontal: "right",
      });
      throw error;
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const getCustomerEmail = async (customer: string | number) => {
    try {
      const res = await apiClient.getCustomer({
        id: customer,
        countryCode: country?.code,
      });

      if (res.status === 200) {
        const { customer: responseData } = await res.json();

        if (responseData) {
          return responseData.email;
        }
      }

      return null;
    } catch (err) {
      return null;
    }
  };

  const handleApplyCouponCode = async ({ stripeCouponCode = "" }) => {
    if (stripeCouponCode) {
      const code = stripeCouponCode.trim();
      try {
        const res = await apiClient.getPromotionCode({
          promotionCode: code,
          countryCode: country?.code,
        });
        if (res.status !== 200) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }
        const { promotionCodes: responseData } = await res.json();
        if (!responseData) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        const { data } = responseData;
        if (!data || !data[0] || code !== data[0].code) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        const {
          id: promoId,
          coupon: couponObject,
          max_redemptions: max_redemptions_promotion_code,
          times_redeemed: times_redeemed_promotion_code,
          customer,
          restrictions,
          expires_at,
        } = data[0];
        if (!couponObject && !couponObject.id) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["NO_PROMO_EXIST"]);
        }
        if (
          max_redemptions_promotion_code &&
          times_redeemed_promotion_code &&
          max_redemptions_promotion_code <= times_redeemed_promotion_code
        ) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["EXCEED_USE_PROMO"]);
        }

        const { max_redemptions: max_redemptions_coupon, times_redeemed: times_redeemed_coupon } = couponObject;
        if (max_redemptions_coupon && times_redeemed_coupon && max_redemptions_coupon <= times_redeemed_coupon) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["EXCEED_USE_COUPON"]);
        }

        if (customer) {
          const customerEmail = await getCustomerEmail(customer);

          if (customerEmail !== user.email) {
            throw new Error(COUPON_CODE_ERROR_MESSAGE["NO_PERMISSION"]);
          }
          customerIdRef.current = customer;
        }

        /**
         * if coupon is fixed discount and has different currency
         */
        if (
          couponObject.amount_off > 0 &&
          couponObject.currency &&
          couponObject.currency.toUpperCase() !== getCurrencyCode(country).toUpperCase()
        ) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["INVALID_CURRENCY"]);
        }

        if (couponObject.duration_in_months && couponObject.created) {
          const expiredDate = new Date(Number(couponObject.created) * 1000);
          expiredDate.setMonth(expiredDate.getMonth() + Number(couponObject.duration_in_months));
          if (expiredDate < new Date()) {
            throw new Error(COUPON_CODE_ERROR_MESSAGE["COUPON_EXPRIED"]);
          }
        }

        /**
         * this promo code has some restrictions such as 'minimum_amount'
         */
        if (restrictions) {
          const { minimum_amount, minimum_amount_currency, first_time_transaction } = restrictions;

          if (minimum_amount) {
            if (minimum_amount / 100 > subtotal) {
              throw new Error(COUPON_CODE_ERROR_MESSAGE["MINIMUM_ORDER"]);
            }

            if (
              minimum_amount > 0 &&
              minimum_amount_currency &&
              minimum_amount_currency.toUpperCase() !== getCurrencyCode(country).toUpperCase()
            ) {
              throw new Error(COUPON_CODE_ERROR_MESSAGE["INVALID_CURRENCY_IN_RESTRICTION"]);
            }
          }

          if (first_time_transaction) {
            // TO DO: SAVE THIS ON STRIPE CUSTOMER METADATA OR ON THE OPPIZI USER
            const didReedemed = !!localStorage.getItem(`first_time_transaction_${promoId}`);
            if (didReedemed) {
              throw new Error(COUPON_CODE_ERROR_MESSAGE["ONLY_FIRST"]);
            }
          }
        }

        if (expires_at) {
          if (new Date().getTime() / 1000 > expires_at) {
            throw new Error(COUPON_CODE_ERROR_MESSAGE["PROMO_EXPIRED"]);
          }
        }
        /**
         * if percent_off is a positive float larger than 0, and smaller or equal to 100,
         * that represents the discount the coupon will apply
         *
         * amount_off is a positive integer representing the amount to subtract from an invoice total
         *
         * amount_off = subtotal * percent_off / 100
         */
        let percent_off: number | null = null;
        let amount_off: number | null = null;
        let flag = false;

        if (couponObject.percent_off > 0 && couponObject.percent_off < 100) {
          percent_off = couponObject.percent_off;
          amount_off = (subtotal * percent_off!) / 100;
          amount_off = Math.round(Number((((subtotal * percent_off!) / 100) * 100).toFixed(2))) / 100;
          flag = true;
        }
        if (couponObject.amount_off > 0 && couponObject.amount_off < subtotal * 100) {
          amount_off = Math.round(Number(((couponObject.amount_off / 100) * 100).toFixed(2))) / 100;
          flag = true;
        }
        if (!flag) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["INCORRECT_DISCOUNT_VALUE"]);
        }

        if (subtotal && amount_off && subtotal - amount_off <= 0.5) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["LESS_THAN"]);
        }

        runSnackBar({
          type: "success",
          vertical: "top",
          msg: `Your ${percent_off}% discount has been successfully applied!`,
        });

        promotionIdRef.current = promoId;
        setCouponErrorMessage("");
        return true;
      } catch (err) {
        setCouponErrorMessage((err as any)?.message);
      }
    }

    // in case invalid or empty code
    onUpdateCampaignCostWithCoupon(null, null, null);
    promotionIdRef.current = "";
    customerIdRef.current = "";

    return false;
  };

  const onSubmit = async () => {
    if (isSubmittingRef.current || submitting) {
      return;
    }

    setSubmitting(true);
    try {
      await handleSubmit();
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
    }
  };

  // Server-authoritative breakdown via `useBillingPreview`.
  // The hook owns the fingerprint freshness gate, debounced preview call,
  // and error handling — see its source for the contract. The drawer-open
  // gate (`showCheckout`) is significant: it also triggers the BE City
  // Metrics sync, which depends on pathfinder data populated by
  // `updatePathfinderData()` inside `handleSubmit`.
  const currency = country ? getCurrencyCode(country) : "USD";
  // HUB-498: the same intent the legacy checkout sends as `discount_choice`,
  // reused here so the OADS billing preview + charge apply the review discount.
  // Derived from the drawer's exclusivity choice + the flag, so it's `true`
  // only when the review discount is genuinely the order's chosen discount.
  const applyReviewDiscount =
    deriveCheckoutDiscountChoice({
      isReviewDiscountEnabled,
      discountChoice,
      hasStripeCoupon: !!stripeCouponCode,
    }) === "review";
  const { costBreakdown, breakdownReady } = useBillingPreview({
    subtotal,
    currency,
    campaignId,
    couponCode: stripeCouponCode,
    applyReviewDiscount,
    showCheckout,
    onError: () => {
      runSnackBar({
        type: "error",
        msg: "Couldn't load price breakdown. Please try again.",
        vertical: "bottom",
        horizontal: "right",
      });
    },
  });

  return {
    fetchStripeCheckout,
    onSubmit,
    submitting,
    showCheckout,
    setShowCheckout,
    couponErrorMessage,
    costBreakdown,
    breakdownReady,
    couponCode: stripeCouponCode || undefined,
    applyReviewDiscount,
    campaignId: campaignId || "",
    // Do not default to "US" — propagating undefined keeps
    // `useCountryStripe` from loading the US publishable key for a
    // non-US client while the campaign country is still loading, which
    // routed Stripe.js to the wrong account and produced "No such
    // payment_intent" 404s.
    countryCode: country?.code,
  };
};
